/**
 * GA4 + Facebook Pixel analytics module.
 *
 * Dynamically loads scripts based on admin-configured IDs from
 * GET /api/analytics/config. Provides trackEvent() for custom
 * BBPS / Payout / Signup events that fire to both GA4 and FB Pixel.
 */

let _ga4Id = "";
let _fbPixelId = "";
let _customEvents = {};
let _initialized = false;

/* ── Script loaders ─────────────────────────────────────────────── */

function loadGA4(measurementId) {
  if (!measurementId || document.getElementById("ga4-gtag-script")) return;
  const s = document.createElement("script");
  s.id = "ga4-gtag-script";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", measurementId, { send_page_view: false });
}

function loadFBPixel(pixelId) {
  if (!pixelId || window.fbq) return;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", pixelId);
}

/* ── Public API ──────────────────────────────────────────────────── */

/** Call once with the response from GET /api/analytics/config */
export function initAnalytics(config) {
  if (_initialized) return;
  if (!config || !config.enabled) return;

  _ga4Id = config.ga4_measurement_id || "";
  _fbPixelId = config.fb_pixel_id || "";
  _customEvents = config.custom_events || {};

  if (_ga4Id) loadGA4(_ga4Id);
  if (_fbPixelId) loadFBPixel(_fbPixelId);
  _initialized = true;
}

/** Track page view — call on every route change */
export function trackPageView(path, title) {
  if (_ga4Id && window.gtag) {
    window.gtag("event", "page_view", {
      page_path: path,
      page_title: title || document.title,
    });
  }
  if (_fbPixelId && window.fbq) {
    window.fbq("track", "PageView");
  }
}

/**
 * Track a custom event to both GA4 and Facebook Pixel.
 *
 * Event names used in the app:
 *   signup_started, signup_completed,
 *   bill_payment_initiated, bill_payment_completed,
 *   payout_initiated, payout_completed,
 *   wallet_topup, lead
 */
export function trackEvent(eventName, params = {}) {
  const key = eventName.toLowerCase().replace(/[^a-z0-9]/g, "_");
  if (_customEvents[key] === false) return;

  // GA4 — send as-is
  if (_ga4Id && window.gtag) {
    window.gtag("event", eventName, params);
  }

  // FB Pixel — map to standard events where possible
  if (_fbPixelId && window.fbq) {
    const FB_MAP = {
      signup_started: "InitiateCheckout",
      signup_completed: "CompleteRegistration",
      bill_payment_initiated: "InitiateCheckout",
      bill_payment_completed: "Purchase",
      payout_initiated: "InitiateCheckout",
      payout_completed: "Purchase",
      wallet_topup: "AddPaymentInfo",
      lead: "Lead",
    };
    const fbStd = FB_MAP[key];
    if (fbStd) {
      window.fbq("track", fbStd, params);
    } else {
      window.fbq("trackCustom", eventName, params);
    }
  }
}

/** True when at least one tracker is loaded */
export function isActive() {
  return _initialized && (!!_ga4Id || !!_fbPixelId);
}
